import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Area, AreaChart, Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fmtMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

type Tab = "holdings" | "profit" | "allocation";

const ASSETS = [
  { id: "bitcoin", symbol: "BTC", weight: 0.45, color: "#f7931a" },
  { id: "ethereum", symbol: "ETH", weight: 0.25, color: "#627eea" },
  { id: "solana", symbol: "SOL", weight: 0.15, color: "#14f195" },
  { id: "tether", symbol: "USDT", weight: 0.1, color: "#26a17b" },
  { id: "dogecoin", symbol: "DOGE", weight: 0.05, color: "#c2a633" },
] as const;

type MarketRow = {
  id: string;
  current_price: number;
  price_change_percentage_24h: number;
  sparkline_in_7d?: { price: number[] };
};

function useMarket() {
  return useQuery({
    queryKey: ["cg-markets", ASSETS.map((a) => a.id).join(",")],
    queryFn: async (): Promise<MarketRow[]> => {
      const ids = ASSETS.map((a) => a.id).join(",");
      const url = `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids}&sparkline=true&price_change_percentage=24h`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("market fetch failed");
      return res.json();
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function PortfolioPanel({ balance }: { balance: number }) {
  const [tab, setTab] = useState<Tab>("holdings");
  const { data: market = [] } = useMarket();

  const rows = useMemo(() => {
    return ASSETS.map((a) => {
      const m = market.find((x) => x.id === a.id);
      const value = balance * a.weight;
      const price = m?.current_price ?? 0;
      const qty = price > 0 ? value / price : 0;
      const chg = m?.price_change_percentage_24h ?? 0;
      const profit = value * (chg / 100);
      return { ...a, price, qty, value, chg, profit, spark: m?.sparkline_in_7d?.price ?? [] };
    });
  }, [market, balance]);

  const totalProfit24h = rows.reduce((s, r) => s + r.profit, 0);
  const totalProfitPct = balance > 0 ? (totalProfit24h / balance) * 100 : 0;

  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-start justify-between mb-1">
        <div>
          <div className="text-sm text-muted-foreground">Portfolio Value</div>
          <div className="font-mono text-3xl font-semibold tracking-tight">{fmtMoney(balance)}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs mb-4">
        <span className="text-muted-foreground">24h:</span>
        <span className={cn("font-mono", totalProfit24h >= 0 ? "text-emerald-400" : "text-red-400")}>
          {totalProfit24h >= 0 ? "+" : ""}{fmtMoney(totalProfit24h)} ({totalProfitPct.toFixed(2)}%)
        </span>
      </div>

      <div className="flex gap-2 mb-4">
        {([
          { id: "holdings", label: "Holdings" },
          { id: "profit", label: "24h Profit" },
          { id: "allocation", label: "Allocation" },
        ] as { id: Tab; label: string }[]).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              "px-3 py-1.5 rounded-lg text-sm transition-colors",
              tab === t.id ? "bg-white/15 text-foreground font-medium" : "bg-white/5 text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="h-48 mb-4">
        {tab === "holdings" && <HoldingsChart rows={rows} />}
        {tab === "profit" && <ProfitChart rows={rows} />}
        {tab === "allocation" && <AllocationChart rows={rows} />}
      </div>

      <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-[11px] uppercase tracking-wide text-muted-foreground px-1 pb-2 border-b border-white/5">
        <span>Asset</span>
        <span className="text-right">Price</span>
        <span className="text-right">24h</span>
      </div>
      <div className="divide-y divide-white/5">
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-[1fr_auto_auto] gap-2 py-3 items-center">
            <div className="flex items-center gap-2.5">
              <span className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: `${r.color}22`, color: r.color }}>
                {r.symbol.slice(0, 3)}
              </span>
              <div>
                <div className="text-sm font-medium">{r.symbol}</div>
                <div className="text-[11px] text-muted-foreground font-mono">{r.qty.toFixed(r.symbol === "USDT" ? 2 : 4)}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-sm">{fmtMoney(r.price, r.price < 1 ? 4 : 2)}</div>
              <div className="font-mono text-[11px] text-muted-foreground">{fmtMoney(r.value)}</div>
            </div>
            <div className={cn("text-right font-mono text-sm", r.chg >= 0 ? "text-emerald-400" : "text-red-400")}>
              {r.chg >= 0 ? "▲" : "▼"} {Math.abs(r.chg).toFixed(2)}%
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HoldingsChart({ rows }: { rows: Row[] }) {
  const series = useMemo(() => {
    const len = Math.min(...rows.map((r) => r.spark.length).filter((n) => n > 0), 168);
    if (!isFinite(len) || len === 0) return [];
    return Array.from({ length: len }).map((_, i) => {
      let v = 0;
      for (const r of rows) {
        const s = r.spark;
        if (!s.length) continue;
        const idx = Math.floor((i / len) * s.length);
        const ratio = s[idx] / s[s.length - 1];
        v += r.value * ratio;
      }
      return { i, v };
    });
  }, [rows]);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={series}>
        <defs>
          <linearGradient id="pf-g" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="hsl(35 95% 55%)" stopOpacity={0.4} />
            <stop offset="100%" stopColor="hsl(35 95% 55%)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis dataKey="i" hide />
        <YAxis hide domain={["dataMin", "dataMax"]} />
        <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} labelFormatter={() => ""} formatter={(v: number) => [fmtMoney(v), "Value"]} />
        <Area type="monotone" dataKey="v" stroke="hsl(35 95% 55%)" strokeWidth={2} fill="url(#pf-g)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function ProfitChart({ rows }: { rows: Row[] }) {
  const data = rows.map((r) => ({ name: r.symbol, profit: Number(r.profit.toFixed(2)), color: r.color }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <XAxis dataKey="name" tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={false} tickLine={false} width={50} tickFormatter={(v) => `$${v}`} />
        <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} formatter={(v: number) => [fmtMoney(v), "24h P/L"]} />
        <Bar dataKey="profit" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => (
            <Cell key={i} fill={d.profit >= 0 ? "#10b981" : "#ef4444"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function AllocationChart({ rows }: { rows: Row[] }) {
  const data = rows.map((r) => ({ name: r.symbol, value: Number(r.value.toFixed(2)), color: r.color }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} formatter={(v: number, n) => [fmtMoney(v), n as string]} />
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="none">
          {data.map((d, i) => <Cell key={i} fill={d.color} />)}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

type Row = {
  id: string; symbol: string; weight: number; color: string;
  price: number; qty: number; value: number; chg: number; profit: number; spark: number[];
};